/**
 * RequireAdmin — route wrapper that limits a page to authenticated
 * admin users only.
 *
 * Operator directive · Public-App unhide pass (2026-02-09):
 *   "Admin stays reachable but only Admin can view."
 *
 * Behaviour:
 *   • No token → redirect to `/` (public LoginPage).
 *   • Token but /api/auth/verify.is_admin === false → redirect to `/`.
 *   • Token AND is_admin === true → render children.
 *   • While the check is inflight, render a minimal "Verifying access…"
 *     placeholder so the page doesn't flash the gated surface.
 *
 * The verify call re-uses the same endpoint App.js already hits on
 * mount, so no additional cost to the backend beyond the one extra
 * request when a non-admin user probes /knowledge-controls or
 * /knowledge-atlas directly.
 */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { API, getToken } from '../utils/authClient';

export function RequireAdmin({ children }) {
  // 'checking' | 'admin' | 'denied'
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const token = getToken();
    if (!token) { setStatus('denied'); return; }

    let cancelled = false;
    fetch(`${API}/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('verify-failed'))))
      .then((data) => { if (!cancelled) setStatus(data?.is_admin ? 'admin' : 'denied'); })
      .catch(() => { if (!cancelled) setStatus('denied'); });

    return () => { cancelled = true; };
  }, []);

  if (status === 'checking') {
    return (
      <div
        data-testid="require-admin-checking"
        className="flex min-h-screen items-center justify-center bg-black"
      >
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400/70">
          Verifying access…
        </div>
      </div>
    );
  }

  if (status === 'denied') return <Navigate to="/" replace />;

  return children;
}

export default RequireAdmin;
